import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedLink } from '@/hooks/useLocalizedLink';
import { useShopifyCartStore } from '@/stores/shopifyCartStore';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShoppingBag, Trash2, Minus, Plus, ExternalLink, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const CartDrawer = () => {
  const { t } = useTranslation('common');
  const { localizeLink } = useLocalizedLink();
  
  const { 
    items, 
    isOpen, 
    isLoading, 
    isSyncing,
    closeCart, 
    clearCart,
    updateQuantity,
    removeItem,
    getCheckoutUrl,
    syncCart,
    itemsCount,
    totalAmount
  } = useShopifyCartStore();

  const count = itemsCount();
  const total = totalAmount();

  // Sync cart with Shopify when drawer opens
  useEffect(() => {
    if (isOpen) {
      syncCart();
    }
  }, [isOpen, syncCart]);

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
      closeCart();
    }
  };

  const handleQuantityChange = async (variantId: string, newQuantity: number) => {
    await updateQuantity(variantId, newQuantity);
  };

  const handleRemoveItem = async (variantId: string) => {
    await removeItem(variantId);
  };

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            {t('cart.title')} ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <ShoppingBag className="h-16 w-16 text-muted-foreground/50" />
            <div>
              <p className="text-lg font-medium">{t('checkout.emptyCart')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('checkout.emptyCartMessage')}
              </p>
            </div>
            <Link to={localizeLink('/shop')} onClick={closeCart}>
              <Button>{t('checkout.discoverShop')}</Button>
            </Link>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {items.map((item) => (
                  <div key={item.variantId} className="flex gap-4 p-2 rounded-lg bg-muted/30">
                    <div className="w-16 h-16 bg-background rounded-md overflow-hidden flex-shrink-0">
                      {item.product.node.images?.edges?.[0]?.node && (
                        <img 
                          src={item.product.node.images.edges[0].node.url} 
                          alt={item.product.node.title} 
                          className="w-full h-full object-cover" 
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate text-sm">{item.product.node.title}</h4>
                      <p className="text-xs text-muted-foreground">
                        {item.selectedOptions.map(option => option.value).join(' • ')}
                      </p>
                      <p className="font-semibold text-sm mt-1">
                        {item.price.currencyCode} {parseFloat(item.price.amount).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6" 
                        onClick={() => handleRemoveItem(item.variantId)}
                        disabled={isLoading}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => handleQuantityChange(item.variantId, item.quantity - 1)}
                          disabled={isLoading}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => handleQuantityChange(item.variantId, item.quantity + 1)}
                          disabled={isLoading}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex-shrink-0 space-y-4 pt-4 border-t bg-background">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('checkout.subtotal')}</span>
                  <span className="font-medium">
                    {items[0]?.price.currencyCode || '€'} {total.toFixed(2)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-base font-semibold">
                  <span>{t('checkout.total')}</span>
                  <span>{items[0]?.price.currencyCode || '€'} {total.toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Button 
                  onClick={handleCheckout} 
                  className="w-full" 
                  size="lg"
                  disabled={items.length === 0 || isLoading || isSyncing}
                >
                  {isLoading || isSyncing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('checkout.order')}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={clearCart}
                  disabled={items.length === 0 || isLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('checkout.clearCart')}
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
